import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { setEmployeeToken, setEmployeeUser } from '../../lib/employeeAuth';
import { getNextPathAfterLogin, clearSessionExpiryFlag } from '../../lib/sessionRedirect';
import { storeSessionTokens } from '../../lib/refreshToken';

/**
 * Employee Google-Auth landing page.
 *
 * Reached whenever `/employee/*#session_id=<sid>` — swaps the sid for the
 * employee JWT and lands on the dashboard (or /complete-profile).
 *
 * Now also stores the long-lived refresh token so Google-authed employees
 * get the same silent-refresh treatment as password-authed ones.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function EmployeeAuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      toast.error('Google sign-in failed — no session id returned');
      navigate('/employee/login');
      return;
    }

    (async () => {
      try {
        const res = await axios.post('/api/employee/auth/google/session', {
          session_id: sessionId,
        });
        setEmployeeToken(res.data.token);
        setEmployeeUser(res.data.employee);
        // Store the refresh token so subsequent 401s can silently refresh
        // just like the password login path.
        storeSessionTokens('employee', res.data);
        clearSessionExpiryFlag();
        window.history.replaceState(null, '', window.location.pathname);
        toast.success('Signed in with Google');
        if (res.data.profile_completed === false) {
          navigate('/employee/complete-profile');
        } else {
          navigate(getNextPathAfterLogin('/employee/dashboard'), { replace: true });
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Google sign-in failed');
        navigate('/employee/login');
      }
    })();
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#111111]"
      data-testid="employee-auth-callback"
    >
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#F59E0B] mx-auto" />
        <p className="mt-4 text-neutral-400 text-sm">Finishing sign-in…</p>
      </div>
    </div>
  );
}
